import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FavoriteBorder as SupportIcon,
  Share as ShareIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  TrendingUp as OngoingIcon,
  CardGiftcard as QuickDonateIcon,
} from '@mui/icons-material';
import { DONATION_AMOUNTS, DONATION_CONFIG } from '../../constants/donationAmounts';

/**
 * FloatingActionButton Component for Home Page
 * Circular button design with support submenu
 */
const FloatingActionButton = ({
  centerName,
  config,
  theme,
  showSearch = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSupportSubmenu, setShowSupportSubmenu] = useState(false);
  const [shouldPulse, setShouldPulse] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  // Donation UI states
  const [showDonationUI, setShowDonationUI] = useState(false);
  const [selectedDonationAmount, setSelectedDonationAmount] = useState(null);
  const [customDonationAmount, setCustomDonationAmount] = useState('');
  const [isDonationProcessing, setIsDonationProcessing] = useState(false);
  const [donationError, setDonationError] = useState(null);

  // Whether center has opted into support features
  const supportEnabled = config?.support_enabled === true;

  // Check if a hex color is too light to use as button background
  const isLightColor = (hex) => {
    if (!hex) return true;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7;
  };

  // Safe accent color — falls back to primary if secondary is too light
  const accentColor = (!isLightColor(theme?.colors?.secondary))
    ? theme?.colors?.secondary
    : (theme?.colors?.primary || '#3B82F6');

  const menuRef = useRef(null);
  const navigate = useNavigate();
  
  // Check if we're on mobile
  const isMobile = window.innerWidth <= 768;
  
  // Add pulse animation every 5 seconds when not expanded
  useEffect(() => {
    if (!isExpanded && !showSupportSubmenu) {
      const interval = setInterval(() => {
        setShouldPulse(true);
        setTimeout(() => setShouldPulse(false), 600);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isExpanded, showSupportSubmenu]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsExpanded(false);
        setShowSupportSubmenu(false);
      }
    };
    
    if (isExpanded || showSupportSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isExpanded, showSupportSubmenu]);
  
  // Handle share functionality — captures screenshot of [data-share-capture]
  // element on the current page (e.g. lecture header) and attaches it to the
  // Web Share payload. Falls back gracefully when no capture target exists,
  // when capture fails, or when the platform doesn't accept file shares.
  const handleShare = async () => {
    setIsSharing(true);

    const captureEl = typeof document !== 'undefined'
      ? document.querySelector('[data-share-capture]')
      : null;

    const fallbackTitle = centerName || config?.name || 'Community Center';
    const fallbackText = `Check out ${centerName || config?.name || 'our community center'} - Your hub for events, lectures, and spiritual content`;
    const shareData = {
      title: captureEl?.dataset?.shareTitle || fallbackTitle,
      text: captureEl?.dataset?.shareText || fallbackText,
      url: window.location.href,
    };

    let shareFile = null;

    try {
      // Try to capture the designated element as a PNG
      if (captureEl && navigator.canShare) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const bgColor = captureEl.dataset?.shareCaptureBg || '#ffffff';
          const canvas = await html2canvas(captureEl, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: bgColor,
            logging: false,
          });
          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, 'image/png', 0.95)
          );
          if (blob) {
            const slug = (shareData.title || 'share')
              .replace(/[^a-z0-9]/gi, '-')
              .toLowerCase()
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
              .slice(0, 60) || 'share';
            shareFile = new File([blob], `${slug}.png`, { type: 'image/png' });
          }
        } catch (captureErr) {
          // Capture failed (e.g. tainted canvas) — silently share without image
          console.warn('Share capture failed, sharing without image:', captureErr);
        }
      }

      // Prefer file share when available
      if (shareFile && navigator.canShare && navigator.canShare({ files: [shareFile] })) {
        await navigator.share({ ...shareData, files: [shareFile] });
      } else if (navigator.share && isMobile) {
        await navigator.share(shareData);
      } else if (shareFile) {
        // Desktop with capture: download image + copy link
        const objectUrl = URL.createObjectURL(shareFile);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = shareFile.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
        await navigator.clipboard.writeText(shareData.url);
        showToast('Image saved & link copied to clipboard!');
      } else {
        await navigator.clipboard.writeText(shareData.url);
        showToast('Link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error sharing:', err);
        showToast('Failed to share. Please try again.', true);
      }
    } finally {
      setIsSharing(false);
    }
  };
  
  // Handle support button click
  const handleSupportClick = () => {
    setShowSupportSubmenu(true);
  };
  
  // Handle ongoing support navigation
  const handleOngoingSupport = () => {
    navigate('/support');
    setShowSupportSubmenu(false);
    setIsExpanded(false);
  };
  
  // Handle quick donate - checks for external donation URL first, then falls back to modal
  const handleQuickDonate = () => {
    const donationUrl = config?.donation_page_url || config?.support_options?.stripe?.link;
    if (donationUrl) {
      window.open(donationUrl, '_blank');
      setShowSupportSubmenu(false);
      setIsExpanded(false);
      return;
    }
    setShowDonationUI(true);
    setShowSupportSubmenu(false);
    setIsExpanded(false);
    setSelectedDonationAmount(null);
    setCustomDonationAmount('');
    setDonationError(null);
  };
  
  // Process donation with selected amount
  const processDonation = async () => {
    setDonationError(null);
    setIsDonationProcessing(true);
    
    // Determine the final amount
    const finalAmount = customDonationAmount ? parseFloat(customDonationAmount) : selectedDonationAmount;
    
    if (!finalAmount || finalAmount < 1) {
      setDonationError('Please select or enter a valid amount (minimum $1)');
      setIsDonationProcessing(false);
      return;
    }
    
    try {
      const donationCenterCode = config?.code || 'DEZ';
      const donationCenterName = centerName || config?.name || 'Community Center';

      const response = await fetch('/.netlify/functions/create-donation-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: finalAmount,
          announcementId: '',
          announcementTitle: `Quick Donation to ${donationCenterName}`,
          centerCode: donationCenterCode,
          centerName: donationCenterName,
          donorEmail: ''
        })
      });
      
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('No checkout URL received');
        setDonationError('Unable to process donation. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setDonationError('Unable to process donation. Please try again.');
    } finally {
      setIsDonationProcessing(false);
    }
  };
  
  // Close donation UI
  const closeDonationUI = () => {
    setShowDonationUI(false);
    setSelectedDonationAmount(null);
    setCustomDonationAmount('');
    setDonationError(null);
  };
  
  // Show toast notification
  const showToast = (message, isError = false) => {
    const toast = document.createElement('div');
    toast.className = 'fab-toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 140px;
      left: 50%;
      transform: translateX(-50%);
      background-color: ${isError ? '#f44336' : '#333'};
      color: white;
      padding: 12px 24px;
      border-radius: 24px;
      z-index: 9999;
      animation: slideUp 0.3s ease-out;
      font-size: 14px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    `;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 2500);
  };
  
  // Calculate button positions using polar coordinates
  const getButtonPosition = (position, radius = 90) => {
    let angle;
    switch(position) {
      case 'share': // Straight up position for Share
        angle = 270; // 270° = straight up (12 o'clock)
        break;
      case 'support': // Straight left for Support
        angle = 180; // 180° = 9 o'clock position (straight left)
        break;
      case 'ongoing': // Same as share position for consistency
        angle = 270; // Straight up, same as Share
        break;
      case 'quick': // Same as support position for consistency
        angle = 180; // Straight left, same as Support
        break;
      case 'search': // Upper-left diagonal
        angle = 225; // 225° = between straight up and straight left
        break;
      default:
        angle = 180;
    }
    
    const radians = (angle * Math.PI) / 180;
    
    return {
      x: Math.cos(radians) * radius,
      y: Math.sin(radians) * radius
    };
  };
  
  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  const fabVariants = {
    closed: { 
      scale: 1
    },
    open: { 
      scale: 1.1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20
      }
    },
    pulse: {
      scale: [1, 1.15, 1],
      boxShadow: [
        '0 4px 12px rgba(0,0,0,0.15)',
        '0 8px 25px rgba(0,0,0,0.25)',
        '0 4px 12px rgba(0,0,0,0.15)'
      ],
      transition: {
        duration: 0.6,
        ease: "easeInOut"
      }
    }
  };
  
  const circularButtonVariants = {
    hidden: { 
      scale: 0,
      opacity: 0
    },
    visible: (custom) => ({
      scale: 1,
      opacity: 1,
      x: custom.x,
      y: custom.y,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: custom.delay
      }
    }),
    hover: {
      scale: 1.15,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    },
    tap: {
      scale: 0.95
    }
  };
  
  // Styles
  const styles = {
    container: {
      position: 'fixed',
      bottom: isMobile ? '80px' : '80px', // Raised to avoid bottom menu
      right: '24px',
      zIndex: 9999 // Very high to ensure it's on top
    },
    fabWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002 // Above backdrop
    },
    fab: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      backgroundColor: theme?.colors?.primary || '#0f172a',
      color: 'white',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      fontSize: '24px',
      position: 'relative',
      zIndex: 1003, // Highest z-index for main FAB
      outline: 'none',
      boxShadow: (isExpanded || showSupportSubmenu)
        ? '0 10px 30px rgba(0,0,0,0.3)' 
        : '0 4px 12px rgba(0,0,0,0.15)'
    },
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.2)', // Reduced opacity
      // Removed backdrop filter to prevent blur issues
      zIndex: 998 // Lower than buttons
    },
    circularButton: {
      position: 'absolute',
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: 'white',
      border: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      outline: 'none',
      padding: '10px',
      gap: '3px',
      zIndex: 1001 // Above backdrop
    },
    circularButtonIcon: {
      fontSize: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    circularButtonText: {
      fontSize: '10px',
      fontWeight: '600',
      whiteSpace: 'nowrap'
    },
    submenuButton: {
      width: '56px',
      height: '56px',
      backgroundColor: 'white', // Solid white for better visibility
      border: '1px solid rgba(0, 0, 0, 0.1)', // Add subtle border for definition
      zIndex: 1001 // Ensure submenu buttons are above backdrop
    }
  };
  
  // Add CSS animation styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      @keyframes slideUp {
        from { transform: translateX(-50%) translateY(20px); opacity: 0; }
        to { transform: translateX(-50%) translateY(0); opacity: 1; }
      }
      @keyframes slideDown {
        from { transform: translateX(-50%) translateY(0); opacity: 1; }
        to { transform: translateX(-50%) translateY(20px); opacity: 0; }
      }
      @keyframes rotateIn {
        from { transform: rotate(-180deg) scale(0); }
        to { transform: rotate(0deg) scale(1); }
      }
    `;
    document.head.appendChild(styleElement);
    return () => document.head.removeChild(styleElement);
  }, []);
  
  // Main menu button positions
  const mainMenuPositions = showSearch
    ? [
        { ...getButtonPosition('share', 100), delay: 0 },
        { ...getButtonPosition('search', 100), delay: 0.05 },
        { ...getButtonPosition('support', 100), delay: 0.1 },
      ]
    : [
        { ...getButtonPosition('share', 100), delay: 0 },
        { ...getButtonPosition('support', 100), delay: 0.1 },
      ];
  
  // Support submenu positions - same pattern as main menu for consistency
  // Calculated from FAB center, not from Support button
  const submenuPositions = [
    { ...getButtonPosition('ongoing', 100), delay: 0 }, // Ongoing at 270° (straight up, same as Share)
    { ...getButtonPosition('quick', 100), delay: 0.1 } // Quick at 180° (straight left, same as Support)
  ];
  
  return (
    <div ref={menuRef} style={styles.container}>
      {/* Backdrop */}
      <AnimatePresence>
        {(isExpanded || showSupportSubmenu) && (
          <motion.div 
            style={styles.backdrop} 
            onClick={() => {
              setIsExpanded(false);
              setShowSupportSubmenu(false);
            }}
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={backdropVariants}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>
      
      <div style={styles.fabWrapper}>
        {/* Main Menu - Share and Support Buttons */}
        <AnimatePresence>
          {isExpanded && !showSupportSubmenu && (
            <>
              {/* Share Button */}
              <motion.button
                style={{
                  ...styles.circularButton,
                  backgroundColor: accentColor,
                  zIndex: 1001 // Ensure it's above backdrop
                }}
                initial="hidden"
                animate="visible"
                exit="hidden"
                custom={mainMenuPositions[0]}
                variants={circularButtonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handleShare}
                disabled={isSharing}
              >
                <div style={{ ...styles.circularButtonIcon, color: 'white' }}>
                  {isSharing ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <ShareIcon />
                    </motion.div>
                  ) : (
                    <ShareIcon />
                  )}
                </div>
                <span style={{ ...styles.circularButtonText, color: 'white' }}>
                  Share
                </span>
              </motion.button>
              
              {/* Search Button — only on lectures page */}
              {showSearch && (
                <motion.button
                  style={{
                    ...styles.circularButton,
                    backgroundColor: theme?.colors?.primary || '#1E40AF',
                    zIndex: 1001
                  }}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  custom={mainMenuPositions[1]}
                  variants={circularButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  onClick={() => {
                    setIsExpanded(false);
                    navigate(`/lectures/search${window.location.search}`);
                  }}
                >
                  <div style={{ ...styles.circularButtonIcon, color: 'white' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="11" cy="11" r="8" fill="none" stroke="currentColor" strokeWidth="2"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <span style={{ ...styles.circularButtonText, color: 'white' }}>
                    Search
                  </span>
                </motion.button>
              )}

              {/* Support Button — only shown when support is enabled */}
              {supportEnabled && (
                <motion.button
                  style={{
                    ...styles.circularButton,
                    backgroundColor: theme?.colors?.primary || '#1E40AF',
                    zIndex: 1001
                  }}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  custom={mainMenuPositions[showSearch ? 2 : 1]}
                  variants={circularButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                  onClick={handleSupportClick}
                >
                  <div style={{ ...styles.circularButtonIcon, color: 'white' }}>
                    <SupportIcon />
                  </div>
                  <span style={{ ...styles.circularButtonText, color: 'white' }}>
                    Support
                  </span>
                </motion.button>
              )}
            </>
          )}
        </AnimatePresence>
        
        {/* Support Submenu - Shows when Support is clicked */}
        <AnimatePresence>
          {showSupportSubmenu && (
            <>
              
              {/* Ongoing Support Button */}
              <motion.button
                style={{
                  ...styles.circularButton,
                  ...styles.submenuButton
                }}
                initial="hidden"
                animate="visible"
                exit="hidden"
                custom={submenuPositions[0]}
                variants={circularButtonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handleOngoingSupport}
              >
                <div style={{ ...styles.circularButtonIcon, color: theme?.colors?.primary || '#6366F1', fontSize: '22px' }}>
                  <OngoingIcon />
                </div>
                <span style={{ ...styles.circularButtonText, color: '#1f2937', fontSize: '8px' }}>
                  Ongoing
                </span>
              </motion.button>
              
              {/* Quick Donate Button */}
              <motion.button
                style={{
                  ...styles.circularButton,
                  ...styles.submenuButton
                }}
                initial="hidden"
                animate="visible"
                exit="hidden"
                custom={submenuPositions[1]}
                variants={circularButtonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handleQuickDonate}
              >
                <div style={{ ...styles.circularButtonIcon, color: accentColor, fontSize: '22px' }}>
                  <QuickDonateIcon />
                </div>
                <span style={{ ...styles.circularButtonText, color: '#1f2937', fontSize: '8px' }}>
                  Quick
                </span>
              </motion.button>
            </>
          )}
        </AnimatePresence>
        
        {/* Main FAB Button */}
        <motion.button
          style={styles.fab}
          onClick={() => {
            if (showSupportSubmenu) {
              setShowSupportSubmenu(false);
            } else {
              setIsExpanded(!isExpanded);
            }
          }}
          variants={fabVariants}
          initial="closed"
          animate={[
            (isExpanded || showSupportSubmenu) ? "open" : "closed",
            shouldPulse && !isExpanded && !showSupportSubmenu ? "pulse" : {}
          ]}
          whileHover={{ 
            scale: 1.1,
            boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
            filter: 'brightness(1.1)'
          }}
          whileTap={{ scale: 0.95 }}
          aria-label="Support and share options"
          title="Support & Share"
        >
          {(isExpanded || showSupportSubmenu) ? (
            <CloseIcon style={{ fontSize: 28 }} />
          ) : (
            <motion.div
              animate={shouldPulse ? { rotate: [0, 10, -10, 10, 0] } : {}}
              transition={{ duration: 0.6 }}
            >
              <AutoAwesomeIcon style={{ fontSize: 26 }} />
            </motion.div>
          )}
        </motion.button>
      </div>
      
      {/* Donation Amount Selection Modal */}
      {showDonationUI && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            position: 'relative'
          }}>
            {/* Close button */}
            <button
              onClick={closeDonationUI}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CloseIcon />
            </button>
            
            {/* Header */}
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '20px',
              fontWeight: '600',
              color: '#1f2937'
            }}>
              Quick Donation
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              color: '#6b7280'
            }}>
              {`Support ${centerName || config?.name || 'our community'}`}
            </p>
            
            {/* Error Message */}
            {donationError && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                border: '1px solid #fecaca'
              }}>
                {donationError}
              </div>
            )}
            
            {/* Suggested Amounts */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginBottom: '20px'
            }}>
              {DONATION_AMOUNTS.default.map(amount => (
                <button
                  key={amount}
                  onClick={() => {
                    setSelectedDonationAmount(amount);
                    setCustomDonationAmount('');
                    setDonationError(null);
                  }}
                  disabled={isDonationProcessing}
                  style={{
                    padding: '14px',
                    fontSize: '18px',
                    fontWeight: '600',
                    background: selectedDonationAmount === amount 
                      ? (theme?.colors?.primary || '#0f172a')
                      : 'white',
                    color: selectedDonationAmount === amount 
                      ? 'white' 
                      : '#374151',
                    border: `2px solid ${selectedDonationAmount === amount 
                      ? (theme?.colors?.primary || '#0f172a')
                      : '#e5e7eb'}`,
                    borderRadius: '8px',
                    cursor: isDonationProcessing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: isDonationProcessing ? 0.5 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isDonationProcessing && selectedDonationAmount !== amount) {
                      e.currentTarget.style.borderColor = theme?.colors?.primary || '#0f172a';
                      e.currentTarget.style.background = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDonationProcessing && selectedDonationAmount !== amount) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.background = 'white';
                    }
                  }}
                >
                  ${amount}
                </button>
              ))}
            </div>

            {/* Tax-deductible Helper Text */}
            {DONATION_CONFIG.showTaxDeductibleText && (
              <p style={{
                fontSize: '12px',
                color: '#888',
                textAlign: 'center',
                marginTop: '-12px',
                marginBottom: '16px'
              }}>
                {DONATION_CONFIG.taxDeductibleMessage}
              </p>
            )}

            {/* Custom Amount */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                color: '#6b7280',
                fontWeight: '500'
              }}>
                Or enter a custom amount:
              </label>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: 'white',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden'
              }}>
                <span style={{
                  padding: '0 12px',
                  fontSize: '18px',
                  color: '#6b7280',
                  background: '#f9fafb',
                  borderRight: '1px solid #e5e7eb',
                  height: '44px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  $
                </span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Enter amount"
                  value={customDonationAmount}
                  onChange={(e) => {
                    setCustomDonationAmount(e.target.value);
                    setSelectedDonationAmount(null);
                    setDonationError(null);
                  }}
                  disabled={isDonationProcessing}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    fontSize: '16px',
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    opacity: isDonationProcessing ? 0.5 : 1
                  }}
                />
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px'
            }}>
              <button
                onClick={closeDonationUI}
                disabled={isDonationProcessing}
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  background: 'white',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: isDonationProcessing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  opacity: isDonationProcessing ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={processDonation}
                disabled={isDonationProcessing || (!selectedDonationAmount && !customDonationAmount)}
                style={{
                  flex: 2,
                  padding: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: theme?.colors?.primary || '#0f172a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (isDonationProcessing || (!selectedDonationAmount && !customDonationAmount)) 
                    ? 'not-allowed' 
                    : 'pointer',
                  transition: 'all 0.2s',
                  opacity: (isDonationProcessing || (!selectedDonationAmount && !customDonationAmount)) 
                    ? 0.5 
                    : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isDonationProcessing ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      style={{ width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%' }}
                    />
                    Processing...
                  </>
                ) : (
                  <>Continue to Checkout</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingActionButton;